import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useMemo,
  useState
} from "react"

import { Box } from "../Box"
import type { IconName } from "../Icon"
import { Toast } from "."

interface ToastConfig {
  variant?: "inline" | "card"
  anchor?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  seconds?: number
  action?: {
    title: string
    icon?: IconName
    onClick: () => void
  }
  icon?: IconName
}

export function ToastProvider(props: PropsWithChildren) {
  const { children } = props

  const [toast, setToast] = useState<ToastConfigWithKey>({
    title: "",
    key: 0
  })

  const [open, setOpen] = useState(false)

  const {
    key,
    variant = "inline",
    title,
    subtitle,
    seconds,
    action,
    icon = "check",
    anchor = <Box position="fixed" bottom={24} width="100%" />
  } = toast

  const value = useMemo(
    () => ({
      showToast: (config: ToastConfig) => {
        setToast({ ...defaultConfig, ...config, key: Date.now() })
        setOpen(true)
      }
    }),
    []
  )

  return (
    <>
      <Toast
        key={key}
        variant={variant}
        anchor={anchor}
        open={open}
        icon={icon}
        title={title}
        subtitle={subtitle}
        seconds={seconds}
        action={action}
        onOpenChange={setOpen}
      />
      <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
    </>
  )
}

const defaultConfig: Partial<ToastConfig> = {
  seconds: 5
}

export function useToast() {
  const context = useContext(ToastContext)

  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider")
  }

  return context
}

interface ToastConfigWithKey extends ToastConfig {
  key: number
}

interface ToastContextValue {
  showToast: (config: ToastConfig) => void
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {}
})
